import { always, converge, propOr, T, F } from 'ramda';
import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { MAIN_SETUP } from 'app/features/deltaConfig/constants';
import { USER_FETCHED } from 'app/features/user/actionTypes';
import {
  SELECTED_SKU_RESET,
  SKU_SELECTED,
  SKIP_FREE_TRIAL_SET,
  SKIP_FREE_TRIAL_RESET,
  PRODUCTS_FETCHED,
} from 'app/features/products/actionTypes';
import { FETCH_DELTA_CONFIG_SUCCESS } from 'app/features/deltaConfig/actionTypes';
import {
  pathFromPayload,
  pathOrFromPayload,
  pickPayload,
  pickState,
} from 'app/reducers/helpers';
import {
  tierNameMap,
  countryCodeTierMap,
  regionByCurrencyMap,
} from 'app/features/products/constants';
import threeTiersROWCRPlus from 'app/features/products/tiers/threeTiersROWCR+';

// Helper function to update benefits for the matched SKU
const updateBenefitsForSKU = (products, storeBenefits) => {
  return products.map(product => {
    const newBenefit = storeBenefits.find(
      benefit => benefit.sku === product.sku,
    );

    if (!newBenefit) return product;

    const updatedBenefits = product.benefits.map(benefit => {
      if (benefit.titleId === 'product_benefit_store_off') {
        return {
          ...benefit,
          isHidden: false,
          values: {
            ...benefit.values,
            ...newBenefit.values,
            region: regionByCurrencyMap(newBenefit.values.currency),
          },
        };
      }
      return benefit;
    });

    return {
      ...product,
      benefits: updatedBenefits,
    };
  });
};

const products = handleActions(
  {
    [USER_FETCHED]: (state, { payload: { countryCode } }) =>
      countryCodeTierMap[countryCode] || threeTiersROWCRPlus,
    [FETCH_DELTA_CONFIG_SUCCESS]: converge(propOr, [
      pickState,
      pathFromPayload([MAIN_SETUP, 'tierNameKey']),
      always(tierNameMap),
    ]),
    [PRODUCTS_FETCHED]: (state, { payload }) =>
      payload?.storeBenefits
        ? updateBenefitsForSKU(state, payload.storeBenefits)
        : state,
  },
  [],
);

const freeTrialDuration = handleActions(
  {
    [PRODUCTS_FETCHED]: pathOrFromPayload('P7D', ['freeTrialDuration']),
  },
  'P7D',
);

const selectedSKU = handleActions(
  {
    [SKU_SELECTED]: pickPayload,
    [SELECTED_SKU_RESET]: always(''),
  },
  '',
);

const isSkipFreeTrial = handleActions(
  {
    [SKIP_FREE_TRIAL_SET]: T,
    [SKIP_FREE_TRIAL_RESET]: F,
  },
  false,
);

const persistConfig = {
  storage,
  key: 'products',
  whitelist: ['selectedSKU', 'isSkipFreeTrial'],
};

const productsReducer = persistReducer(
  persistConfig,
  combineReducers({
    products,
    selectedSKU,
    isSkipFreeTrial,
    freeTrialDuration,
  }),
);

export { products };

export default productsReducer;
